// The basemap's two themes.
//
// The site follows the system's colour scheme. The chrome turns with the
// stylesheet's tokens; the map cannot — MapLibre paints from literals in the
// style, not from CSS — so its half of each theme lives here, mirrored by hand
// against the tokens the way CHART_THEME is.
//
// Only the ground turns: ocean, land, coast, borders, the settlement tint, the
// labels. Nothing that colours DATA is here. The field, the dots and the wind
// arrows keep their ramps in both themes (variables.ts / colors.ts), because a
// temperature is the same colour whatever the room's lighting.

import { map, hasMap } from './instance.ts';

export const LIGHT_SCHEME = window.matchMedia('(prefers-color-scheme: light)');

export interface MapTheme {
  ocean: string;
  land: string;
  landOpacity: number;
  coast: string;
  coastOpacity: number;
  urban: string;
  green: string;
  roadMajor: string;
  roadHighway: string;
  hillShadow: string;
  hillHighlight: string;
  countryLabel: string;
  cityLabel: string;
  labelHalo: string;
  border: string;
  dotStroke: string;
  /** The field's default wash strength. */
  fieldOpacity: number;
}

export const MAP_THEMES: { dark: MapTheme; light: MapTheme } = {
  dark: {
    ocean: '#0a1626',
    land: '#1c3049',
    landOpacity: 0.9,
    coast: '#5b7da3',
    coastOpacity: 0.6,
    urban: '#3d5c8a',
    green: '#16283d',
    roadMajor: '#6d8bb0',
    roadHighway: '#93b1d6',
    hillShadow: '#000000',
    hillHighlight: '#a9c6e6',
    countryLabel: '#8fa8c4',
    cityLabel: '#eaf2ff',
    labelHalo: 'rgba(4,12,24,0.85)',
    border: '#c3d7ee',
    dotStroke: 'rgba(0,0,0,0.35)',
    fieldOpacity: 0.6,
  },
  light: {
    ocean: '#dbe4ec',
    land: '#f3f1ea',
    landOpacity: 1,
    coast: 'rgba(20,30,40,0.28)',
    coastOpacity: 1,
    urban: '#d8d3c6',
    green: '#e4e8d8',
    roadMajor: '#b7b0a2',
    roadHighway: '#a0988a',
    hillShadow: '#8f8a7e',
    hillHighlight: '#ffffff',
    countryLabel: '#5f6b76',
    cityLabel: '#101418',
    labelHalo: 'rgba(255,255,255,0.9)',
    border: 'rgba(20,30,40,0.14)',
    dotStroke: 'rgba(255,255,255,0.7)',
    fieldOpacity: 0.55,
  },
};

// The reader's pick (Layers → Auto / Light / Dark), which wins over the system.
// It lives on <html data-theme>, set before first paint by the shell's inline
// script and kept in localStorage; "auto" is the absence of both.
export type ThemePick = 'auto' | 'light' | 'dark';
const THEME_KEY = 'fcc.theme';

export function themePick(): ThemePick {
  const t = document.documentElement.dataset.theme;
  return t === 'light' || t === 'dark' ? t : 'auto';
}

export function setThemePick(pick: ThemePick): void {
  if (pick === 'auto') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = pick;
  try {
    if (pick === 'auto') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, pick);
  } catch {
    /* best-effort, like the rest of the persistence */
  }
}

export const isLight = (): boolean => {
  const pick = themePick();
  return pick === 'auto' ? LIGHT_SCHEME.matches : pick === 'light';
};
export const mapTheme = (): MapTheme => (isLight() ? MAP_THEMES.light : MAP_THEMES.dark);

// Every themed paint property, by layer. A layer that is not on this map (no
// vector basemap, no elevation) is skipped.
function paints(t: MapTheme): [string, string, unknown][] {
  return [
    ['ocean', 'background-color', t.ocean],
    ['land', 'fill-color', t.land],
    ['land', 'fill-opacity', t.landOpacity],
    ['ocean-mask-fill', 'fill-color', t.ocean],
    ['mask-islands-fill', 'fill-color', t.land],
    ['mask-islands-fill', 'fill-opacity', t.landOpacity],
    ['land-outline', 'line-color', t.coast],
    ['land-outline', 'line-opacity', t.coastOpacity],
    ['hillshade', 'hillshade-shadow-color', t.hillShadow],
    ['hillshade', 'hillshade-highlight-color', t.hillHighlight],
    ['hillshade', 'hillshade-accent-color', t.ocean],
    ['urban-areas', 'fill-color', t.urban],
    ['landuse-green', 'fill-color', t.green],
    ['landuse-built', 'fill-color', t.urban],
    ['basemap-water', 'fill-color', t.ocean],
    ['roads-major', 'line-color', t.roadMajor],
    ['roads-highway', 'line-color', t.roadHighway],
    ['country-labels', 'text-color', t.countryLabel],
    ['country-labels', 'text-halo-color', t.labelHalo],
    ['country-boundaries', 'line-color', t.border],
    ['country-boundaries-disputed', 'line-color', t.border],
    ['weather-points', 'circle-stroke-color', t.dotStroke],
    ['city-labels', 'text-color', t.cityLabel],
    ['city-labels', 'text-halo-color', t.labelHalo],
    ['city-selected-label', 'text-color', t.cityLabel],
    ['city-selected-label', 'text-halo-color', t.labelHalo],
  ];
}

// Repaint the ground for the current scheme — called when the system scheme
// changes under an open page.
export function applyMapTheme(): void {
  if (!hasMap()) return;
  for (const [id, prop, value] of paints(mapTheme())) {
    if (!map.getLayer(id)) continue;
    try {
      map.setPaintProperty(id, prop, value);
    } catch {
      /* a property this layer type does not take — skip it */
    }
  }
}
